import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateCode, logout as apiLogout } from '../services/api'; // Assuming logout clears server session
import { SessionContext } from '../contexts/SessionContext';
import { getTranslations } from '../utils/translations';
import '../styles/CodeValidation.css';

const LOCAL_STORAGE_KEY = 'doorAccessAppRememberedCode';

const CodeValidationPage: React.FC = () => {
  const [code, setCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { setValidated, setHouseId, validated } = useContext(SessionContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Obtener traducciones según el idioma del navegador
  const translations = getTranslations();

  useEffect(() => {
    // Check URL query parameter first, then localStorage
    const urlCode = searchParams.get('code');
    if (urlCode) {
      setCode(urlCode);
      // Remove the code from the URL and browser history
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      const rememberedCode = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (rememberedCode) {
        setCode(rememberedCode);
      }
    }
  }, [searchParams]);

  // Clear remembered code if session is no longer validated (e.g., after timeout/redirect)
  useEffect(() => {
    if (!validated) {
        // Check if we are on the root page, if not, it means a redirect likely happened due to session expiry
        // Or if an explicit logout happened elsewhere and validated became false.
        // This prevents clearing the code if the user just landed on the page for the first time without a session.
        if (localStorage.getItem(LOCAL_STORAGE_KEY)) {
            console.log("Session not validated, clearing remembered code.");
            // localStorage.removeItem(LOCAL_STORAGE_KEY); // Decided against auto-clearing on !validated to allow re-attempt with remembered code
        }
    }
  }, [validated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError(translations.pleaseEnterCode);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await validateCode(code);
      
      if (response.success) {
        setValidated(true);
        if (response.houseId) {
          setHouseId(response.houseId);
        }
        // Save the code to localStorage on successful validation
        localStorage.setItem(LOCAL_STORAGE_KEY, code);
        navigate('/geo-validation');
      } else if (response.tooEarly) {
        setError(translations.codeValidInNextHours);
      } else {
        setError(response.message || translations.codeValidationError);
        // If validation fails with a remembered code, clear it so user has to re-type
        // This prevents getting stuck with a wrong remembered code.
        if (code === localStorage.getItem(LOCAL_STORAGE_KEY)) {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
        }
      }
    } catch (err) {
      setError(translations.connectionError);
      console.error('Error:', err);
      // Also clear remembered code on server error if it was used
      if (code === localStorage.getItem(LOCAL_STORAGE_KEY)) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearRememberedCode = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    setCode('');
    setError(null); // Clear any errors as well
    // Optionally, call API logout if you want to ensure server session is also cleared
    // apiLogout().then(() => console.log("Server session cleared on manual code clear."));
  };

  return (
    <div className="code-validation-container">
      <div className="code-validation-card">
        <h1>{translations.accessControl}</h1>
        <p>{translations.enterAccessCode}</p>
        
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <input
              type="password" // Changed to password to show dots
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={translations.enterCode}
              disabled={loading}
              autoFocus
              autoComplete="current-password" // Helps browser password managers, but might not be ideal for this custom remember
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? translations.validating : translations.validateCode}
          </button>
          {localStorage.getItem(LOCAL_STORAGE_KEY) && code === localStorage.getItem(LOCAL_STORAGE_KEY) && (
            <button 
              type="button" 
              onClick={handleClearRememberedCode}
              className="clear-button"
              style={{marginTop: "10px", backgroundColor: "#f44336"}} // Simple styling for clear button
              disabled={loading}
            >
              {translations.forgetSavedCode}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default CodeValidationPage;
